import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '@/hooks/useTranslation';
import { previsaoTempoWidget } from '@/lib/widgets';
import {
  Sun,
  Cloud,
  CloudRain,
  CloudLightning,
  CloudFog,
  Snowflake,
  Thermometer,
  Droplets,
  Wind,
  Loader2,
  MapPin,
  ThermometerSun,
  ThermometerSnowflake,
} from 'lucide-react';

/* ═══════════════════════════════════════════
   PREVISÃO DO TEMPO HOJE — Open-Meteo
   Componente reutilizável que exibe a
   previsão do tempo atual com dados reais.
   ═══════════════════════════════════════════ */

export interface PrevisaoTempoHojeProps {
  /** Latitude do local (default: Ilha do Mel) */
  latitude?: number;
  /** Longitude do local (default: Ilha do Mel) */
  longitude?: number;
  /** Nome do local exibido no card (default: "Ilha do Mel") */
  locationName?: string;
}

interface WeatherData {
  current: {
    temp: number;
    feelsLike: number;
    humidity: number;
    windSpeed: number;
    weatherCode: number;
  };
  daily: {
    max: number;
    min: number;
  };
}

const WMO_CODES: Record<
  number,
  {
    label: string;
    labelEn: string;
    labelEs: string;
    labelDe: string;
    labelFr: string;
    icon: React.ReactNode;
  }
> = {
  0: {
    label: 'Céu limpo',
    labelEn: 'Clear sky',
    labelEs: 'Cielo despejado',
    labelDe: 'Klarer Himmel',
    labelFr: 'Ciel dégagé',
    icon: <Sun className="w-6 h-6 text-amber-500" />,
  },
  1: {
    label: 'Principalmente limpo',
    labelEn: 'Mainly clear',
    labelEs: 'Principalmente despejado',
    labelDe: 'Überwiegend klar',
    labelFr: 'Principalement dégagé',
    icon: <Sun className="w-6 h-6 text-amber-400" />,
  },
  2: {
    label: 'Parcialmente nublado',
    labelEn: 'Partly cloudy',
    labelEs: 'Parcialmente nublado',
    labelDe: 'Teilweise bewölkt',
    labelFr: 'Partiellement nuageux',
    icon: <Cloud className="w-6 h-6 text-warm-gray" />,
  },
  3: {
    label: 'Nublado',
    labelEn: 'Overcast',
    labelEs: 'Nublado',
    labelDe: 'Bedeckt',
    labelFr: 'Couvert',
    icon: <Cloud className="w-6 h-6 text-warm-gray" />,
  },
  45: {
    label: 'Nevoeiro',
    labelEn: 'Fog',
    labelEs: 'Niebla',
    labelDe: 'Nebel',
    labelFr: 'Brouillard',
    icon: <CloudFog className="w-6 h-6 text-warm-gray" />,
  },
  48: {
    label: 'Nevoeiro com geada',
    labelEn: 'Depositing rime fog',
    labelEs: 'Niebla con escarcha',
    labelDe: 'Raureifnebel',
    labelFr: 'Brouillard givrant',
    icon: <CloudFog className="w-6 h-6 text-warm-gray" />,
  },
  51: {
    label: 'Garoa leve',
    labelEn: 'Light drizzle',
    labelEs: 'Llovizna ligera',
    labelDe: 'Leichter Nieselregen',
    labelFr: 'Bruine légère',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  53: {
    label: 'Garoa moderada',
    labelEn: 'Moderate drizzle',
    labelEs: 'Llovizna moderada',
    labelDe: 'Mäßiger Nieselregen',
    labelFr: 'Bruine modérée',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  55: {
    label: 'Garoa intensa',
    labelEn: 'Dense drizzle',
    labelEs: 'Llovizna densa',
    labelDe: 'Dichter Nieselregen',
    labelFr: 'Bruine dense',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  61: {
    label: 'Chuva leve',
    labelEn: 'Slight rain',
    labelEs: 'Lluvia ligera',
    labelDe: 'Leichter Regen',
    labelFr: 'Pluie légère',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  63: {
    label: 'Chuva moderada',
    labelEn: 'Moderate rain',
    labelEs: 'Lluvia moderada',
    labelDe: 'Mäßiger Regen',
    labelFr: 'Pluie modérée',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  65: {
    label: 'Chuva forte',
    labelEn: 'Heavy rain',
    labelEs: 'Lluvia intensa',
    labelDe: 'Starker Regen',
    labelFr: 'Forte pluie',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  71: {
    label: 'Neve leve',
    labelEn: 'Slight snow',
    labelEs: 'Nieve ligera',
    labelDe: 'Leichter Schneefall',
    labelFr: 'Neige légère',
    icon: <Snowflake className="w-6 h-6 text-ocean" />,
  },
  73: {
    label: 'Neve moderada',
    labelEn: 'Moderate snow',
    labelEs: 'Nieve moderada',
    labelDe: 'Mäßiger Schneefall',
    labelFr: 'Neige modérée',
    icon: <Snowflake className="w-6 h-6 text-ocean" />,
  },
  75: {
    label: 'Neve intensa',
    labelEn: 'Heavy snow',
    labelEs: 'Nieve intensa',
    labelDe: 'Starker Schneefall',
    labelFr: 'Neige intense',
    icon: <Snowflake className="w-6 h-6 text-ocean" />,
  },
  80: {
    label: 'Pancadas leves',
    labelEn: 'Slight rain showers',
    labelEs: 'Chubascos ligeros',
    labelDe: 'Leichte Regenschauer',
    labelFr: 'Averses légères',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  81: {
    label: 'Pancadas moderadas',
    labelEn: 'Moderate rain showers',
    labelEs: 'Chubascos moderados',
    labelDe: 'Mäßige Regenschauer',
    labelFr: 'Averses modérées',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  82: {
    label: 'Pancadas violentas',
    labelEn: 'Violent rain showers',
    labelEs: 'Chubascos violentos',
    labelDe: 'Heftige Regenschauer',
    labelFr: 'Averses violentes',
    icon: <CloudRain className="w-6 h-6 text-ocean" />,
  },
  95: {
    label: 'Tempestade',
    labelEn: 'Thunderstorm',
    labelEs: 'Tormenta',
    labelDe: 'Gewitter',
    labelFr: 'Orage',
    icon: <CloudLightning className="w-6 h-6 text-ocean" />,
  },
  96: {
    label: 'Tempestade com granizo',
    labelEn: 'Thunderstorm with hail',
    labelEs: 'Tormenta con granizo',
    labelDe: 'Gewitter mit Hagel',
    labelFr: 'Orage avec grêle',
    icon: <CloudLightning className="w-6 h-6 text-ocean" />,
  },
  99: {
    label: 'Tempestade forte com granizo',
    labelEn: 'Heavy thunderstorm with hail',
    labelEs: 'Tormenta fuerte con granizo',
    labelDe: 'Schweres Gewitter mit Hagel',
    labelFr: 'Orage fort avec grêle',
    icon: <CloudLightning className="w-6 h-6 text-ocean" />,
  },
};

function getWeatherInfo(code: number, lang: string) {
  const info = WMO_CODES[code] || WMO_CODES[0];
  switch (lang) {
    case 'en':
      return { label: info.labelEn, icon: info.icon };
    case 'es':
      return { label: info.labelEs, icon: info.icon };
    case 'de':
      return { label: info.labelDe, icon: info.icon };
    case 'fr':
      return { label: info.labelFr, icon: info.icon };
    default:
      return { label: info.label, icon: info.icon };
  }
}

function getLabels(lang: string) {
  const labels: Record<string, Record<string, string>> = {
    'pt-BR': {
      title: 'Tempo agora',
      subtitle: 'Previsão do tempo em tempo real',
      temp: 'Temperatura',
      feelsLike: 'Sensação',
      humidity: 'Umidade',
      wind: 'Vento',
      max: 'Máx',
      min: 'Mín',
      loading: 'Carregando previsão...',
      error: 'Não foi possível carregar a previsão.',
      source: 'Dados: Open-Meteo',
    },
    en: {
      title: 'Weather now',
      subtitle: 'Real-time weather forecast',
      temp: 'Temperature',
      feelsLike: 'Feels like',
      humidity: 'Humidity',
      wind: 'Wind',
      max: 'Max',
      min: 'Min',
      loading: 'Loading forecast...',
      error: 'Unable to load forecast.',
      source: 'Data: Open-Meteo',
    },
    es: {
      title: 'Tiempo ahora',
      subtitle: 'Pronóstico del tiempo en tiempo real',
      temp: 'Temperatura',
      feelsLike: 'Sensación',
      humidity: 'Humedad',
      wind: 'Viento',
      max: 'Máx',
      min: 'Mín',
      loading: 'Cargando pronóstico...',
      error: 'No se pudo cargar el pronóstico.',
      source: 'Datos: Open-Meteo',
    },
    de: {
      title: 'Wetter jetzt',
      subtitle: 'Wettervorhersage in Echtzeit',
      temp: 'Temperatur',
      feelsLike: 'Gefühlt',
      humidity: 'Luftfeuchtigkeit',
      wind: 'Wind',
      max: 'Max',
      min: 'Min',
      loading: 'Vorhersage wird geladen...',
      error: 'Vorhersage konnte nicht geladen werden.',
      source: 'Daten: Open-Meteo',
    },
    fr: {
      title: 'Météo actuelle',
      subtitle: 'Prévisions météo en temps réel',
      temp: 'Température',
      feelsLike: 'Ressenti',
      humidity: 'Humidité',
      wind: 'Vent',
      max: 'Max',
      min: 'Min',
      loading: 'Chargement des prévisions...',
      error: 'Impossible de charger les prévisions.',
      source: 'Données : Open-Meteo',
    },
  };
  return labels[lang] || labels['pt-BR'];
}

const DEFAULT_LAT = -25.55;
const DEFAULT_LON = -48.35;

export function PrevisaoTempoHoje({
  latitude = DEFAULT_LAT,
  longitude = DEFAULT_LON,
  locationName = 'Ilha do Mel',
}: PrevisaoTempoHojeProps) {
  const { lang } = useTranslation();
  const [data, setData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const labels = getLabels(lang);

  useEffect(() => {
    let cancelled = false;

    async function fetchWeather() {
      try {
        const url =
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${latitude}` +
          `&longitude=${longitude}` +
          `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m` +
          `&daily=temperature_2m_max,temperature_2m_min` +
          `&timezone=America%2FSao_Paulo` +
          `&forecast_days=1`;

        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch');
        const json = await res.json();

        if (cancelled) return;

        setData({
          current: {
            temp: Math.round(json.current.temperature_2m),
            feelsLike: Math.round(json.current.apparent_temperature),
            humidity: json.current.relative_humidity_2m,
            windSpeed: Math.round(json.current.wind_speed_10m),
            weatherCode: json.current.weather_code,
          },
          daily: {
            max: Math.round(json.daily.temperature_2m_max[0]),
            min: Math.round(json.daily.temperature_2m_min[0]),
          },
        });
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchWeather();
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 flex items-center gap-3 text-warm-gray">
        <Loader2 className="w-5 h-5 animate-spin text-teal-deep" />
        <span className="font-body text-sm">{labels.loading}</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 text-warm-gray font-body text-sm">
        {labels.error}
      </div>
    );
  }

  const weather = getWeatherInfo(data.current.weatherCode, lang);
  const schema = previsaoTempoWidget.getSchema('https://open-meteo.com/en/docs');

  return (
    <div
      className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      {...previsaoTempoWidget.getDataAttrs()}
    >
      {schema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              ...schema,
            })}
          </script>
        </Helmet>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-deep to-ocean px-6 py-4 text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Thermometer className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-display text-base tracking-wide">{labels.title}</h4>
            <p className="font-body text-[11px] text-teal-100 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {locationName}
            </p>
          </div>
        </div>
        <span className="font-body text-[10px] text-teal-100 opacity-80">{labels.source}</span>
      </div>

      <div className="p-6">
        {/* Principal: temperatura + ícone */}
        <div className="flex items-center gap-5 mb-6">
          <div className="w-16 h-16 rounded-2xl bg-sand-50 border border-sand-200 flex items-center justify-center flex-shrink-0">
            {weather.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl text-warm-dark">{data.current.temp}°</span>
              <span className="font-body text-sm text-warm-gray">
                {labels.feelsLike} {data.current.feelsLike}°
              </span>
            </div>
            <p className="font-body text-sm text-warm-dark font-medium mt-0.5">{weather.label}</p>
          </div>
        </div>

        {/* Grid de detalhes */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-sand-50 rounded-xl border border-sand-200 p-3 text-center flex flex-col items-center justify-center">
            <ThermometerSun className="w-9 h-9 text-orange-800" />
            <p className="font-body text-[14px] text-warm-gray uppercase tracking-wide mb-1">
              {labels.max}
            </p>

            <p className="font-display text-lg text-warm-dark">{data.daily.max}°</p>
          </div>
          <div className="bg-sand-50 rounded-xl border border-sand-200 p-3 text-center flex flex-col items-center justify-center">
            <ThermometerSnowflake className="w-9 h-9 text-blue-800" />
            <p className="font-body text-lg text-warm-gray uppercase tracking-wide mb-1">
              {labels.min}
            </p>
            <p className="font-display text-lg text-warm-dark">{data.daily.min}°</p>
          </div>
          <div className="bg-sand-50 rounded-xl border border-sand-200 p-3 text-center flex flex-col items-center justify-center">
            <Droplets className="w-9 h-9 text-ocean mb-1" />
            <p className="font-body text-lg text-warm-gray uppercase tracking-wide">
              {labels.humidity}
            </p>
            <p className="font-display text-lg text-warm-dark">{data.current.humidity}%</p>
          </div>
          <div className="bg-sand-50 rounded-xl border border-sand-200 p-3 text-center flex flex-col items-center justify-center">
            <Wind className="w-9 h-9 text-teal-deep mb-1" />
            <p className="font-body text-lg text-warm-gray uppercase tracking-wide">
              {labels.wind}
            </p>
            <p className="font-display text-3xl text-warm-dark">
              {data.current.windSpeed} <span className="text-xs font-body">km/h</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
